"use client"

import { useState, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Upload, Eye, EyeOff, X } from "lucide-react"
import { ABILITY_TYPE_CONFIG, type AbilityType, type PublishDraft } from "@/lib/community/types"
import { COMMUNITY_BOARDS } from "@/lib/community/boards"
import { publishPost } from "@/lib/community/service/interaction-service"
import { AbilityTypeBadge } from "./ability-type-badge"
import { useAppContext } from "@/lib/app-context"

/**
 * PublishFlow — single-page form for publishing a new community post.
 */
export function PublishFlow() {
  const navigate = useNavigate()
  const { setWaveIntensity } = useAppContext()

  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [abilityType, setAbilityType] = useState<AbilityType>("skill")
  const [boardSlug, setBoardSlug] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [preview, setPreview] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  /* Dim wave background */
  useEffect(() => {
    setWaveIntensity(0.2)
    return () => setWaveIntensity(1)
  }, [setWaveIntensity])

  const handleAddTag = useCallback(() => {
    const trimmed = tagInput.trim()
    if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed])
      setTagInput("")
    }
  }, [tagInput, tags])

  const handleRemoveTag = useCallback((tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || !description.trim() || !boardSlug) return
    setSubmitting(true)
    try {
      const draft: PublishDraft = {
        title: title.trim(),
        description: description.trim(),
        abilityType,
        boardSlug,
        tags,
        shareMode: "showcase",
      }
      await publishPost(draft)
      setSubmitted(true)
      setTimeout(() => navigate("/community"), 2000)
    } finally {
      setSubmitting(false)
    }
  }, [title, description, abilityType, boardSlug, tags, navigate])

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && boardSlug

  if (submitted) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-3">
        <div className="text-2xl">🎉</div>
        <p className="text-foreground font-medium">发布成功！</p>
        <p className="text-xs text-muted-foreground">正在返回社区…</p>
      </div>
    )
  }

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-20 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            type="button"
            onClick={() => navigate("/community")}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            返回社区
          </button>
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {preview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {preview ? "编辑" : "预览"}
          </button>
        </div>

        <h1 className="text-base font-bold text-foreground mb-6">发布新内容</h1>

        {preview ? (
          /* ── Preview Mode ── */
          <div className="space-y-4">
            <div className="liquid-glass-card rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <AbilityTypeBadge type={abilityType} size="md" />
                {boardSlug && (
                  <span className="text-xs text-muted-foreground">
                    {COMMUNITY_BOARDS.find((b) => b.slug === boardSlug)?.name}
                  </span>
                )}
              </div>
              <h2 className="text-sm font-semibold text-foreground mb-2">
                {title || "（无标题）"}
              </h2>
              <p className="text-sm text-foreground/80 whitespace-pre-line">
                {description || "（无描述）"}
              </p>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary/70 text-foreground/80 px-2 py-0.5 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ── Edit Mode ── */
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                标题 *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="给你的内容起个响亮的名字"
                maxLength={100}
                className="w-full bg-secondary/30 border border-border/40 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 transition-colors"
              />
              <span className="text-micro text-muted-foreground mt-1 block text-right">
                {title.length}/100
              </span>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                描述 *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="详细介绍你的能力，包括使用场景、效果、注意事项等"
                rows={6}
                maxLength={2000}
                className="w-full bg-secondary/30 border border-border/40 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 transition-colors resize-none"
              />
              <span className="text-micro text-muted-foreground mt-1 block text-right">
                {description.length}/2000
              </span>
            </div>

            {/* Ability type */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">
                能力类型 *
              </label>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(ABILITY_TYPE_CONFIG) as AbilityType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAbilityType(type)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      abilityType === type
                        ? `${ABILITY_TYPE_CONFIG[type].bgClass} ${ABILITY_TYPE_CONFIG[type].textClass} ring-1 ring-current/20`
                        : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {ABILITY_TYPE_CONFIG[type].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Board selection */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                发布版块 *
              </label>
              <select
                value={boardSlug}
                onChange={(e) => setBoardSlug(e.target.value)}
                className="w-full bg-secondary/30 border border-border/40 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
              >
                <option value="">选择版块…</option>
                {COMMUNITY_BOARDS.map((board) => (
                  <option key={board.slug} value={board.slug}>
                    {board.name} — {board.slogan}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                标签（最多 5 个）
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 text-xs bg-secondary/70 text-foreground/80 px-2 py-0.5 rounded-full"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              {tags.length < 5 && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleAddTag()
                      }
                    }}
                    placeholder="输入标签后按 Enter"
                    className="flex-1 bg-secondary/30 border border-border/40 rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground bg-secondary/50 rounded-lg transition-colors"
                  >
                    添加
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="mt-8">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                发布中…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                发出去 🐾
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
